import { debug } from '@/shared/lib';

import {
  DocumentInclusion,
  EncounterSchema,
  getSchemasUsingSeries,
  normalizeInclusionValue,
} from '@/entities/templates';

type SchemaDocumentUpdate = {
  template_series_id: string;
  inclusion: DocumentInclusion;
};

type GetTemplateSchemaIdsArgs = {
  schemas: EncounterSchema[];
  seriesId?: string | null;
};

export const getTemplateSchemaIds = ({
  schemas,
  seriesId,
}: GetTemplateSchemaIdsArgs): string[] =>
  seriesId
    ? getSchemasUsingSeries(schemas, seriesId).map((schema) => schema.id)
    : [];

export const diffSchemaMembership = ({
  original,
  selected,
}: {
  original: string[];
  selected: string[];
}): { toAttach: string[]; toDetach: string[] } => ({
  toAttach: selected.filter((id) => !original.includes(id)),
  toDetach: original.filter((id) => !selected.includes(id)),
});

const toUpdates = (schema: EncounterSchema): SchemaDocumentUpdate[] =>
  schema.documents.flatMap((document) => {
    if (!document.template_series_id) {
      debug(
        `Encounter schema ${schema.id} contains a document without series id, skipping`,
        document,
      );

      return [];
    }

    return [
      {
        template_series_id: document.template_series_id,
        inclusion: normalizeInclusionValue(document.inclusion),
      },
    ];
  });

export const buildSchemaDocuments = ({
  schema,
  templateSeriesId,
  action,
}: {
  schema: EncounterSchema;
  templateSeriesId: string;
  action: 'attach' | 'detach';
}): SchemaDocumentUpdate[] | null => {
  const documents = toUpdates(schema);
  const isAttached = documents.some(
    (document) => document.template_series_id === templateSeriesId,
  );

  if (action === 'attach') {
    return isAttached
      ? null
      : [
          ...documents,
          { template_series_id: templateSeriesId, inclusion: 'on_demand' },
        ];
  }

  return isAttached
    ? documents.filter(
        (document) => document.template_series_id !== templateSeriesId,
      )
    : null;
};
